import React, { useRef, useCallback, memo, useState, useEffect, useMemo } from 'react';
import { View, Pressable, Platform, useWindowDimensions } from 'react-native';
import * as Animatable from 'react-native-animatable';
import styles from '@stylesheet';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import apis from '@api/api';
import useGlobalUser from '@hooks/getGlobalUser';
import parseByDelimiter from '@utils/parseByDelimiter';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultRootState } from '@reduxLocal/persistState';
import { HANDLE_FAVOURITE, SET_ACTIVE_ITEM, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import getDaysAgo from '@utils/getDaysAgo';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { selectUserId, selectFavouriteData, selectUserAuthentications } from '@reduxLocal/selectors/userSelectors';
import { SizableText, XStack, YStack, Card, H4, Text, Popover, Separator } from 'tamagui';
import Button from "@components/UI/Button/Button";
import { FontAwesome5, FontAwesome, Feather, MaterialIcons, Ionicons } from '@expo/vector-icons';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { t } from 'i18next';
import DataChart from '@components/dataTables/DataChart';
import { transformStatsForChart, hasChartData } from '@utils/chartDataTransformer';
import LottieView from 'lottie-react-native';
import { scheduleNotification } from '@utils/notifications/noticeScheduler';
import HuggingFaceExportModal, { HuggingFaceExportModalHandle } from '@components/modals/HuggingFaceExportModal/HuggingFaceExportModal';

const DATA_ITEM_WIDTH = 370; // Match PROJECT_WIDTH for consistent responsive behavior

// Global map to track favouriting state across all DataItem instances
const favouritingMap = new Map<string, boolean>();

// Modern icon mapping for data types
const iconMap = [
  { type: 'misc', icon: <MaterialIcons name="storage" size={20} color="#ffffff" /> },
  { type: 'customer', icon: <Ionicons name="people" size={20} color="#ffffff" /> },
  { type: 'product', icon: <MaterialIcons name="inventory" size={20} color="#ffffff" /> },
  { type: 'inventory', icon: <MaterialIcons name="inventory-2" size={20} color="#ffffff" /> }
];

// Component interfaces
interface DataItemProps {
  item: any;
  index?: number;
  handleShareState?: (item: any) => void;
  handleGenLink?: (item: any) => void;
  handleQuickAdd?: (item: any) => void;
  handleSearch?: (item: any) => void;
  compactMode?: boolean;
  listHorizontal?: boolean;
  handleRefresh?: () => void;
  handleShareModal?: (item: any) => void;
  handleActionModal?: (item: any) => void;
  handleDeleteModal?: (item: any) => void;
  handleSettingsModal?: (item: any) => void;
  availableWidth?: number;
  maxWidth?: number; // Added maxWidth prop for responsive behavior
  shouldShowUnlock?: boolean; // Show unlock button if plan expired or free plan with >3 folders
}

const DataItem: React.FC<DataItemProps> = memo(({
  item,
  index,
  handleShareState,
  handleGenLink,
  handleQuickAdd,
  handleSearch,
  listHorizontal,
  handleRefresh,
  compactMode,
  handleShareModal,
  handleActionModal,
  handleDeleteModal,
  handleSettingsModal,
  availableWidth,
  maxWidth,
  shouldShowUnlock = false
}) => {

    // Hooks and state - Use specific memoized selectors to minimize re-renders
    const userId = useSelector(selectUserId);
    const favouriteDataIds = useSelector(selectFavouriteData, shallowEqual);
    const isFavourite = useMemo(() => favouriteDataIds.includes(item._id), [favouriteDataIds, item._id]);
    const { showConfirm } = useAlertDialog();
    const navigation = useNavigation<any>();
    const globalUser = useGlobalUser();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const theme = themeSelector();
    const { width } = useWindowDimensions();
    const isTablet = width > 768;
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Check for Hugging Face integration (company > user priority)
    const authentications = useSelector(selectUserAuthentications);
    const companyData = useSelector((state: DefaultRootState) => state.promiseData.data?.companyData);
    const hasHuggingFace = useMemo(() => {
      // Priority: company authentications > user authentications
      const companyAuth = companyData?.authentications?.huggingface;
      const userAuth = authentications?.huggingface;
      const hfAuth = companyAuth || userAuth;
      return !!(hfAuth?.token || hfAuth?.username);
    }, [authentications, companyData]);

    const huggingFaceExportRef = useRef<HuggingFaceExportModalHandle>(null);

    // Cleanup favouriting state on unmount
    useEffect(() => {
      return () => {
        favouritingMap.delete(item._id);
      };
    }, [item._id]);


    // Track interaction function
    const trackInteraction = useCallback(async (action: string) => {
      try {
        await apis.trackDataInteraction(item._id, action);
      } catch (error) {
        console.log('Failed to track interaction:', error);
      }
    }, [item._id]);

    // Responsive variables
    const isSmallScreen = width < 600;
    const isMediumScreen = width >= 600 && width < 1024;
    const showCompactMode = isSmallScreen || (maxWidth && maxWidth < 350) || compactMode;

    // Query for folder stats
    const { data: folderStats, refetch, isLoading: statsLoading } = useQuery({
      queryKey: ['folderStats', item._id],
      queryFn: () => apis.getFolderStats(item._id),
      staleTime: 60000, // 1 minute
      enabled: !!item._id,
    });

    // Utility functions
    const isOwner = useCallback(() => {
      return item.owner?._id === userId || item.createdBy === userId;
    }, [item.owner?._id, item.createdBy, userId]);

    const hasPermission = useCallback(() => {
      return item.permission || isOwner();
    }, [item.permission, isOwner]);

    // Event handlers
    const handleEditTitle = useCallback(() => {
      trackInteraction('edit_title');
      showConfirm('Edit Data Title', 'Enter new title for the data', {
        confirmText: 'Save',
        cancelText: "Cancel",
        textField: true,
        initialValue: item.dataTitle,
      }).then(async (res: string) => {
        await apis.changeDataTitle(item._id, res);
        handleRefresh?.();
      });
    }, [item._id, item.dataTitle, handleRefresh, showConfirm, trackInteraction]);

    const handleFavourite = useCallback(async () => {
      // Use global map to prevent duplicate calls across all instances
      if (favouritingMap.get(item._id)) {
        console.log('⚠️ Already favouriting', item._id, '- ignoring duplicate call');
        return;
      }

      const currentState = isFavourite;

      // Mark as processing in global map
      favouritingMap.set(item._id, true);

      // Optimistic update in Redux
      dispatch({ type: HANDLE_FAVOURITE, payload: { id: item._id, type: 'data' } });

      let shouldRollback = false;

      try {
        // Call API to toggle favourite
        await apis.addFavourite(item._id, 'data', {});


        // Invalidate queries but don't refetch immediately to avoid race condition
        // The refetch will happen when DataFavouritesScreen mounts or when explicitly triggered
        queryClient.invalidateQueries({
          queryKey: ['favouriteData'],
          refetchType: 'none' // Don't refetch immediately, just mark as stale
        });

        // Track the interaction
        trackInteraction(currentState ? 'unfavourite' : 'favourite');
      } catch (error: any) {
        console.warn('❌ Failed to update favourite:', error);

        // Handle duplicate request errors silently (they're intentional)
        if (error?.isDuplicate || error?.message?.includes('Duplicate request prevented')) {
          // Don't rollback for duplicates - the first request already went through
          // The optimistic update should stay
        } else {
          // For real errors, mark for rollback
          shouldRollback = true;

          // Show error to user only for non-duplicate errors
          showConfirm(
            'Error',
            'Failed to update favourite. Please try again.',
            {
              confirmText: 'OK',
              hideCancel: true,
            }
          );
        }
      } finally {
        // Rollback if needed
        if (shouldRollback) {
          dispatch({ type: HANDLE_FAVOURITE, payload: { id: item._id, type: 'data' } });
        }

        // Reset loading state after a delay to prevent rapid re-clicks
        setTimeout(() => {
          favouritingMap.delete(item._id);
        }, 800); // Give enough time for API and Redux to settle
      }
    }, [dispatch, item._id, isFavourite, index, queryClient, trackInteraction, showConfirm]);

    const handleNavigateToData = useCallback(() => {
      trackInteraction('edit_data');
      dispatch({ type: SET_ACTIVE_ITEM, payload: item._id });
      navigation.navigate('DataBrowser', {
        requestId: item._id,
        title: item.dataTitle,
        tags: item.dataTags
      });
    }, [dispatch, navigation, item._id, item.dataTitle, item.dataTags, trackInteraction]);

    const handleNavigateToUpgrade = useCallback(() => {
      trackInteraction('unlock_clicked');
      (navigation as any).navigate('Settings', { screen: 'Upgrade' });
    }, [navigation, trackInteraction]);

    const handleNavigateToActions = useCallback(() => {
      trackInteraction('view_actions');
      navigation.navigate('Data', {
        screen: 'ActionHandler',
        params: {
          dataId: item._id, // Use dataId to match route definition
          dataTitle: item.dataTitle
        }
      });
    }, [navigation, item._id, item.dataTitle, trackInteraction]);

    const handleNavigateToUtilities = useCallback(() => {
      trackInteraction('view_utilities');
      navigation.navigate('Data', {
        screen: 'Utilities',
        params: {
          id: item._id,
          dataTitle: item.dataTitle
        }
      });
    }, [navigation, item._id, item.dataTitle, trackInteraction]);


    const handleNavigateToDataHandler = useCallback(() => {
      trackInteraction('open_data');
      navigation.navigate('Data', {
        screen: 'DataHandler',
        params: {
          id: item._id.toString()
        }
      });
    }, [navigation, item._id, trackInteraction]);

    // AI Analysis handler
    const handleAIAnalysis = useCallback(async (force: boolean = false) => {
      setIsMenuOpen(false);
      trackInteraction('ai_analysis');

      // If AI notes already exist, show them
      if (item.aiNotes && item.aiNotes.trim().length > 0 && !force) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: item.aiNotes,
            type: 'ai-notes'
          }
        });
        return;
      }

      // If processing is pending, show status
      if (item.notesPending) {
        showConfirm(
          'AI Analysis in Progress',
          'Your dataset is currently being analyzed. You will be notified when the analysis is complete.',
          {
            confirmText: 'OK',
            hideCancel: true,
          }
        );
        return;
      }

      // Trigger analysis in background
      try {
        // Schedule notification for user
        if (Platform.OS !== 'web') {
          try {
            await scheduleNotification({
              id: `analyse-dataset-${item._id}`,
              title: `Analysing ${parseByDelimiter(item.dataTitle)} in the background`,
              body: `I will notify you once the analysis is complete.`,
              date: new Date(Date.now() + 500)
            });
          } catch (error) {
            console.warn('Failed to schedule notification:', error);
          }
        }

        // Call API to trigger analysis
        await apis.getAINotes(item._id);

        // Invalidate queries to refresh data and show updated status
        queryClient.invalidateQueries({
          queryKey: ['favouriteData'],
          refetchType: 'none'
        });

        // Show success message
        showConfirm(
          'Analysis Started',
          `AI analysis for "${parseByDelimiter(item.dataTitle)}" has been started. You will be notified when it's complete.`,
          {
            confirmText: 'OK',
            hideCancel: true,
          }
        );

        // Refresh the data to show updated status
        handleRefresh?.();
      } catch (error: any) {
        console.error('Failed to start AI analysis:', error);
        showConfirm(
          'Error',
          'Failed to start AI analysis. Please try again later.',
          {
            confirmText: 'OK',
            hideCancel: true,
          }
        );
      }
    }, [item._id, item.aiNotes, item.notesPending, item.dataTitle, dispatch, showConfirm, trackInteraction, handleRefresh]);

    return (
      <View
        style={[
          styles.modernCard,
          {
            backgroundColor: theme ? 'white' : 'rgb(51,51,51)',
            margin: showCompactMode ? 8 : 10,
            // flexGrow: 1,
            marginBottom: 20,
            minHeight: showCompactMode ? 280 : 320,
            // Use availableWidth if provided, otherwise fall back to maxWidth or default
            minWidth: "50%"
          }
        ]}
      >
        {/* Public Badge */}
        {(item.isPublic || item.public === true) && (
          <View style={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: '#10b981',
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 6,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            zIndex: 10,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
          }}>
            <MaterialIcons name="public" size={12} color="white" />
            <SizableText fontSize={10} color="white" fontWeight="600">
              Public
            </SizableText>
          </View>
        )}

        {/* Header Section */}
        <View style={{
          paddingHorizontal: showCompactMode ? 12 : 16,
          borderBottomWidth: 1,
          alignSelf: 'stretch',
          width: '100%',
          justifyContent: 'space-between',
          borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Avatar */}
          {/* <AvatarHandler
            avatar={item.owner?.profile_information?.avatar?.img}
            avatarImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
              borderWidth: 2,
              top: -20,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
            replacementImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
            }}
          /> */}

          {/* Title and Metadata */}
          <View style={{ flex: 1, top: Platform.select({ native: -10 }) }}>
            <XStack alignItems="center" gap={8}>
              <H4
                numberOfLines={1}
                color={theme ? "#1e293b" : "#f1f5f9"}
                fontSize={showCompactMode ? 16 : 18}
                fontWeight="600"
                flex={1}
              >
                {parseByDelimiter(item.dataTitle)}
              </H4>

            </XStack>

            <XStack alignItems="center" gap={8} top={-6}>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {getDaysAgo(item.updatedAt)}
              </SizableText>
              <View style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme ? "#64748b" : "#94a3b8",
              }} />
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {folderStats?.count || item.count || 0} items
              </SizableText>
              {(item.viewCount > 0 || item.interactionCount > 0) && (
                <>
                  <View style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: theme ? "#64748b" : "#94a3b8",
                  }} />
                  <SizableText
                    fontSize={12}
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    {item.viewCount || 0}👁 {item.interactionCount || 0}⚡
                  </SizableText>
                </>
              )}
            </XStack>
          </View>

          {/* Actions Menu */}
          <XStack maxHeight={30} alignSelf="stretch" justifyContent='space-between' flex={1} marginBottom={5} gap={8}>
            <Pressable style={{
              padding: 8,
              borderRadius: 20,
              backgroundColor: theme ? '#f8fafc' : '#1e293b',
            }} onPress={handleEditTitle}>
              <MaterialIcons
                name="edit"
                size={16}
                color={theme ? "#64748b" : "#94a3b8"}
              />
            </Pressable>
            <Pressable style={{
              padding: 8,
              borderRadius: 20,
              backgroundColor: theme ? '#f8fafc' : '#1e293b',
            }} onPress={() => handleGenLink(item)}>
              <MaterialIcons
                name="qr-code"
                size={16}
                color={theme ? "#64748b" : "#94a3b8"}
              />
            </Pressable>
            {/* AI Summary Indicator */}
            {item.aiNotes && item.aiNotes.trim().length > 0 && (
              <Pressable
                onPress={() => handleAIAnalysis(false)}
                style={{
                  padding: 8,
                  borderRadius: 20,
                  backgroundColor: theme ? '#f3e8ff' : '#3d1a5f',
                  position: 'relative',
                }}
              >
                <View style={{ position: 'relative', width: 16, height: 16 }}>
                  <LottieView
                    source={require('@assets/animations/automation_robot.json')}
                    autoPlay
                    loop={false}
                    style={{
                      width: 16,
                      height: 16,
                      position: 'absolute',
                    }}
                  />
                </View>
              </Pressable>
            )}
            {/* Favorite Button */}
            <Pressable
              onPress={handleFavourite}
              style={{
                padding: 8,
                borderRadius: 20,
                backgroundColor: theme ? '#f8fafc' : '#1e293b',
              }}
            >
              <FontAwesome
                name={isFavourite ? "heart" : "heart-o"}
                size={16}
                color={isFavourite ? "#ef4444" : (theme ? "#64748b" : "#94a3b8")}
              />
            </Pressable>

            {/* Ellipsis Menu */}
            <Popover
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              placement="bottom-end"
            >
              <Popover.Trigger asChild>
                <Pressable
                  style={{
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: theme ? '#f8fafc' : '#1e293b',
                  }}
                >
                  <MaterialIcons
                    name="more-vert"
                    size={16}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                </Pressable>
              </Popover.Trigger>

              <Popover.Content
                borderWidth={1}
                borderColor={theme ? "$borderColor" : "$borderColorDark"}
                backgroundColor={theme ? "$background" : "$backgroundDark"}
                padding={0}
                minWidth={180}
                borderRadius={12}
                elevation={5}
              >
                <YStack>
                  {/* Document Count */}
                  <View style={{
                    padding: 12,
                    alignItems: 'center',
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}>
                    <SizableText fontSize={18} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                      {folderStats?.count || item.count || 0}
                    </SizableText>
                    <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                      Documents
                    </SizableText>
                  </View>

                  {/* Stats Section */}
                  <View style={{
                    flexDirection: 'row',
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}>
                    <View style={{
                      flex: 1,
                      padding: 12,
                      alignItems: 'center',
                      borderRightWidth: 1,
                      borderRightColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    }}>
                      <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                        {item.viewCount || 0}
                      </SizableText>
                      <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                        Views
                      </SizableText>
                    </View>
                    <View style={{
                      flex: 1,
                      padding: 12,
                      alignItems: 'center',
                    }}>
                      <SizableText fontSize={16} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                        {item.interactionCount || 0}
                      </SizableText>
                      <SizableText fontSize={10} color={theme ? "#64748b" : "#94a3b8"}>
                        Interactions
                      </SizableText>
                    </View>
                  </View>



                  {/* Actions */}
                  {hasPermission() && (
                    <XStack gap={20}>
                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          handleNavigateToActions();
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="settings"
                          size={18}
                          color={theme ? "#059669" : "#10b981"}
                        />
                        <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                          Actions
                        </SizableText>
                      </Pressable>
                      {
                        Platform.OS === 'web' && (
                          <Pressable
                            onPress={() => {
                              setIsMenuOpen(false);
                              handleNavigateToUtilities();
                            }}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              padding: 12,
                              gap: 12,
                            }}
                          >
                            <MaterialIcons
                              name="code"
                              size={18}
                              color={theme ? "#8b5cf6" : "#a78bfa"}
                            />
                            <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                              Configure Utilities
                            </SizableText>
                          </Pressable>
                        )
                      }

                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />
                    </XStack>
                  )}

                  {/* Settings */}
                  {hasPermission() && (
                    <>
                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          handleSettingsModal?.(item);
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="settings"
                          size={18}
                          color={theme ? "#7c3aed" : "#a78bfa"}
                        />
                        <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                          Settings
                        </SizableText>
                      </Pressable>

                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />
                    </>
                  )}

                  {/* Generate Link */}
                  {hasPermission() && (
                    <>
                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          handleGenLink?.(item);
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="link"
                          size={18}
                          color={theme ? "#f59e0b" : "#fbbf24"}
                        />
                        <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                          Generate Link
                        </SizableText>
                      </Pressable>

                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />
                    </>
                  )}

                  {/* Hugging Face Export */}
                  {hasPermission() && hasHuggingFace && (
                    <>
                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          huggingFaceExportRef.current?.openModal(item._id);
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="model-training"
                          size={18}
                          color="#FFD21E"
                        />
                        <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                          Export to Hugging Face
                        </SizableText>
                      </Pressable>

                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />
                    </>
                  )}

                  {/* AI Analysis */}
                  {hasPermission() && (
                    <XStack gap={12}>
                      <Pressable
                        onPress={() => handleAIAnalysis(false)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          flex: 3,
                          gap: 12,
                        }}
                      >
                        <View style={{ position: 'relative', width: 18, height: 18 }}>
                          <LottieView
                            source={require('@assets/animations/automation_robot.json')}
                            autoPlay
                            loop={!item.aiNotes && !item.notesPending}
                            style={{
                              width: 18,
                              height: 18,
                              position: 'absolute',
                            }}
                          />
                        </View>
                        <SizableText fontSize={14} color={theme ? "#8b5cf6" : "#a78bfa"}>
                          {item.aiNotes ? 'View AI Analysis' : item.notesPending ? 'Analysis Processing...' : 'AI Analysis'}
                        </SizableText>
                      </Pressable>


                      <Pressable
                        onPress={() => handleAIAnalysis(true)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          flex: .5,
                          gap: 12,
                        }}
                      >
                        <FontAwesome
                          name="refresh"
                          size={18}
                          color={theme ? "#6366f1" : "#818cf8"}
                        />
                      </Pressable>
                    </XStack>
                  )}

                  <View style={{
                    height: 1,
                    backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                    marginHorizontal: 12,
                  }} />
                  {/* Search */}
                  <Pressable
                    onPress={() => {
                      setIsMenuOpen(false);
                      handleSearch?.(item);
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 12,
                      gap: 12,
                    }}
                  >
                    <MaterialIcons
                      name="search"
                      size={18}
                      color={theme ? "#6366f1" : "#818cf8"}
                    />
                    <SizableText fontSize={14} color={theme ? "#1e293b" : "#f1f5f9"}>
                      Search
                    </SizableText>
                  </Pressable>

                  {/* Delete */}
                  {hasPermission() && (
                    <>
                      <View style={{
                        height: 1,
                        backgroundColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                        marginHorizontal: 12,
                      }} />

                      <Pressable
                        onPress={() => {
                          setIsMenuOpen(false);
                          handleDeleteModal?.(item);
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          gap: 12,
                        }}
                      >
                        <MaterialIcons
                          name="delete"
                          size={18}
                          color="#ef4444"
                        />
                        <SizableText fontSize={14} color="#ef4444">
                          Delete
                        </SizableText>
                      </Pressable>
                    </>
                  )}
                </YStack>
              </Popover.Content>
            </Popover>
          </XStack>
        </View>

        {/* Content Section */}
        <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
          {/* Data Type Icons */}
          {item.dataTags && (
            <XStack gap={8} marginBottom={12}>
              {iconMap.map((iconItem) => {
                const hasTag = Array.isArray(item.dataTags)
                  ? item.dataTags.includes(iconItem.type)
                  : typeof item.dataTags === 'string'
                    ? item.dataTags.includes(iconItem.type)
                    : false;

                if (hasTag) {
                  return (
                    <View
                      key={iconItem.type}
                      style={{
                        backgroundColor: theme ? '#e2e8f0' : '#334155',
                        padding: 6,
                        borderRadius: 8,
                      }}
                    >
                      {iconItem.icon}
                    </View>
                  );
                }
                return null;
              })}
            </XStack>
          )}

          {/* Stats Row */}
          <XStack justifyContent="space-between" alignItems="center" marginBottom={16} overflow="hidden" maxWidth="100%">
            <YStack gap={16}>
              <View style={{ alignItems: 'center' }}>
                <MaterialIcons
                  name="folder"
                  size={20}
                  color={theme ? "#3b82f6" : "#60a5fa"}
                />
                <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                  Files
                </SizableText>
              </View>

              <View style={{ alignItems: 'center' }}>
                <MaterialIcons
                  name="people"
                  size={20}
                  color={theme ? "#059669" : "#10b981"}
                />
                <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                  {folderStats?.contributorList?.length || 0}
                </SizableText>
              </View>

              <View style={{ alignItems: 'center' }}>
                <MaterialIcons
                  name="visibility"
                  size={20}
                  color={theme ? "#7c3aed" : "#a78bfa"}
                />
                <SizableText fontSize={12} color={theme ? "#64748b" : "#94a3b8"}>
                  Views
                </SizableText>
              </View>
            </YStack>

            {/* Data Chart - Only show if we have actual data */}
            {(() => {
              // Transform the data for native
              const chartData = Platform.OS === 'web'
                ? folderStats?.statsStructure
                : transformStatsForChart(folderStats?.statsStructure);

              // On web, always try to show chart (it will handle empty state)
              if (Platform.OS === 'web') {
                return (
                  <View style={{ flex: 1, maxWidth: '100%', overflow: 'hidden' }}>
                    <DataChart chartOnly hideLabels reportData={chartData || {}} state="month" />
                  </View>
                );
              }

              // On native, only show chart if we have real data
              if (hasChartData(chartData)) {
                return (
                  <View style={{ flex: 1, maxWidth: '100%', overflow: 'hidden' }}>
                    <DataChart chartOnly hideLabels reportData={chartData} state="month" />
                  </View>
                );
              }

              // Show empty state on native when no data
              console.log('⚠️ Showing empty state - no chart data');
              return (
                <View style={{ alignItems: 'center', paddingVertical: 8 }}>
                  <MaterialIcons
                    name="insert-chart"
                    size={16}
                    color={theme ? "#cbd5e1" : "#475569"}
                  />
                  <SizableText fontSize={10} color={theme ? "#cbd5e1" : "#475569"}>
                    No data yet
                  </SizableText>
                </View>
              );
            })()}

            {/* Owner Indicator */}
            {!isOwner() && (
              <View style={{
                backgroundColor: '#10b981',
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 12,
              }}>
                <SizableText fontSize={10} color="white" fontWeight="600">
                  Shared
                </SizableText>
              </View>
            )}
          </XStack>

          {/* Tags */}
          {item.dataTags && typeof item.dataTags === 'string' && (
            <XStack gap={6} flexWrap="wrap" marginBottom={16}>
              {item.dataTags.split(',').slice(0, 3).map((tag: string, idx: number) => (
                <View
                  key={idx}
                  style={{
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 8,
                  }}
                >
                  <SizableText fontSize={11} color={theme ? "#475569" : "#cbd5e1"}>
                    {tag.trim()}
                  </SizableText>
                </View>
              ))}
            </XStack>
          )}
          {/* Handle array case for dataTags */}
          {item.dataTags && Array.isArray(item.dataTags) && (
            <XStack gap={6} flexWrap="wrap" marginBottom={16}>
              {item.dataTags.slice(0, 3).map((tag: string, idx: number) => (
                <View
                  key={idx}
                  style={{
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 8,
                  }}
                >
                  <SizableText fontSize={11} color={theme ? "#475569" : "#cbd5e1"}>
                    {typeof tag === 'string' ? tag.trim() : String(tag).trim()}
                  </SizableText>
                </View>
              ))}
            </XStack>
          )}
        </View>

        {/* Action Buttons */}
        <View style={{
          flexDirection: showCompactMode ? 'column' : 'row',
          padding: showCompactMode ? 12 : 16,
          gap: showCompactMode ? 8 : 12,
          borderTopWidth: 1,
          borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Quick Actions */}
          <XStack gap={8} flex={showCompactMode ? 3 : 1} flexWrap="wrap">
            <Pressable
              onPress={() => handleQuickAdd?.(item)}
              disabled={!hasPermission()}
              style={{
                flex: 1,
                minWidth: 70,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8,
                borderRadius: 8,
                backgroundColor: hasPermission()
                  ? (theme ? '#f0f9ff' : '#0c4a6e')
                  : (theme ? '#f1f5f9' : '#1e293b'),
                opacity: hasPermission() ? 1 : 0.5,
              }}
            >
              <MaterialIcons
                name="add"
                size={16}
                color={hasPermission() ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#94a3b8' : '#64748b')}
              />
              <SizableText
                fontSize={12}
                color={hasPermission() ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#94a3b8' : '#64748b')}
                marginLeft={4}
              >
                Add
              </SizableText>
            </Pressable>

            <Pressable
              onPress={handleNavigateToActions}
              disabled={!hasPermission()}
              style={{
                flex: 1,
                minWidth: 70,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8,
                borderRadius: 8,
                backgroundColor: hasPermission()
                  ? (theme ? '#f0fdf4' : '#064e3b')
                  : (theme ? '#f1f5f9' : '#1e293b'),
                opacity: hasPermission() ? 1 : 0.5,
              }}
            >
              <MaterialIcons
                name="settings"
                size={16}
                color={hasPermission() ? (theme ? '#059669' : '#10b981') : (theme ? '#94a3b8' : '#64748b')}
              />
              <SizableText
                fontSize={12}
                color={hasPermission() ? (theme ? '#059669' : '#10b981') : (theme ? '#94a3b8' : '#64748b')}
                marginLeft={4}
              >
                Actions
              </SizableText>
            </Pressable>

            {
              Platform.OS === 'web' && (
                <Pressable
                  onPress={handleNavigateToUtilities}
                  disabled={!hasPermission()}
                  style={{
                    flex: 1,
                    minWidth: 80,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: hasPermission()
                      ? (theme ? '#f3e8ff' : '#3d1a5f')
                      : (theme ? '#f1f5f9' : '#1e293b'),
                    opacity: hasPermission() ? 1 : 0.5,
                  }}
                >
                  <MaterialIcons
                    name="code"
                    size={16}
                    color={hasPermission() ? (theme ? '#8b5cf6' : '#a78bfa') : (theme ? '#94a3b8' : '#64748b')}
                  />
                  <SizableText
                    fontSize={12}
                    color={hasPermission() ? (theme ? '#8b5cf6' : '#a78bfa') : (theme ? '#94a3b8' : '#64748b')}
                    marginLeft={4}
                  >
                    Utilities
                  </SizableText>
                </Pressable>
              )
            }
            <Pressable
              onPress={handleNavigateToDataHandler}
              disabled={!hasPermission()}
              style={{
                flex: 1,
                minWidth: 90,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8,
                maxWidth: 90,
                borderRadius: 8,
                backgroundColor: hasPermission()
                  ? (theme ? '#f3e8ff' : '#3d1a5f')
                  : (theme ? '#f1f5f9' : '#1e293b'),
                opacity: hasPermission() ? 1 : 0.5,
              }}
            >
              <MaterialIcons
                name="edit"
                size={16}
                color={hasPermission() ? (theme ? '#8b5cf6' : '#a78bfa') : (theme ? '#94a3b8' : '#64748b')}
              />
              <SizableText
                fontSize={12}
                color={hasPermission() ? (theme ? '#8b5cf6' : '#a78bfa') : (theme ? '#94a3b8' : '#64748b')}
                marginLeft={4}
              >
                Edit Model
              </SizableText>
            </Pressable>

          </XStack>

          {/* Primary Action */}
          <Button
            onPress={shouldShowUnlock ? handleNavigateToUpgrade : handleNavigateToData}
            customStyle={{
              flex: showCompactMode ? undefined : 2,
              minHeight: 40,
              borderRadius: 5,
              backgroundColor: shouldShowUnlock
                ? (theme ? '#f59e0b' : '#d97706')
                : (theme ? '#3b82f6' : '#1d4ed8'),
            }}
            buttonText={shouldShowUnlock ? 'Unlock' : (t('dataStoreFindDataRecentlyAccessedDataOpenButton1') || 'Open Data')}
          />
        </View>

        {/* Hugging Face Export Modal */}
        {hasHuggingFace && (
          <HuggingFaceExportModal ref={huggingFaceExportRef} />
        )}
      </View>
    );
  });

// Comparison function for memo to prevent unnecessary re-renders
const areEqual = (prevProps: DataItemProps, nextProps: DataItemProps) => {
  // Only re-render if the item ID changes or key props change
  return (
    prevProps.item._id === nextProps.item._id &&
    prevProps.item.dataTitle === nextProps.item.dataTitle &&
    prevProps.item.updatedAt === nextProps.item.updatedAt &&
    prevProps.item.count === nextProps.item.count &&
    prevProps.compactMode === nextProps.compactMode &&
    prevProps.listHorizontal === nextProps.listHorizontal &&
    prevProps.maxWidth === nextProps.maxWidth
  );
};

// Export with both names for compatibility
export default memo(DataItem, areEqual);
export const RenderDataItems = memo(DataItem, areEqual);